import React, { createContext, useContext, useState, useCallback } from 'react';

const ToastContext = createContext(null);

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const addToast = useCallback((message, type = 'success', duration = 4000) => {
    const id = Date.now() + Math.random().toString(36).substring(2, 9);
    setToasts((prev) => [...prev, { id, message, type }]);

    if (duration > 0) {
      setTimeout(() => {
        removeToast(id);
      }, duration);
    }
  }, [removeToast]);

  const success = useCallback((msg, duration) => addToast(msg, 'success', duration), [addToast]);
  const error = useCallback((msg, duration) => addToast(msg, 'danger', duration), [addToast]);
  const warning = useCallback((msg, duration) => addToast(msg, 'warning', duration), [addToast]);
  const info = useCallback((msg, duration) => addToast(msg, 'info', duration), [addToast]);

  return (
    <ToastContext.Provider value={{ 
      addToast, 
      removeToast, 
      success, 
      error, 
      warning, 
      info,
      showSuccess: success,
      showError: error,
      showWarning: warning,
      showInfo: info
    }}>
      {children}
      {/* Global Toast Container */}
      <div
        className="toast-container position-fixed top-0 end-0 p-3"
        style={{ zIndex: 99999, pointerEvents: 'none' }}
      >
        {toasts.map((t) => {
          let bgClass = 'bg-success text-white';
          let icon = 'bi-check-circle-fill';
          let title = 'Success';

          if (t.type === 'danger' || t.type === 'error') {
            bgClass = 'bg-danger text-white';
            icon = 'bi-exclamation-triangle-fill';
            title = 'Error';
          } else if (t.type === 'warning') {
            bgClass = 'bg-warning text-dark';
            icon = 'bi-exclamation-circle-fill';
            title = 'Notice';
          } else if (t.type === 'info') {
            bgClass = 'bg-info text-dark';
            icon = 'bi-info-circle-fill';
            title = 'Information';
          }

          return (
            <div
              key={t.id}
              className={`toast show mb-2 shadow-lg border-0 ${bgClass}`}
              role="alert"
              aria-live="assertive"
              aria-atomic="true"
              style={{ pointerEvents: 'auto', minWidth: '320px', borderRadius: '8px' }}
            >
              <div className="d-flex align-items-center p-3">
                <i className={`bi ${icon} fs-4 me-3`}></i>
                <div className="flex-grow-1">
                  <div className="fw-bold fs-6">{title}</div>
                  <div className="small">{t.message}</div>
                </div>
                <button
                  type="button"
                  className={`btn-close ${t.type === 'warning' || t.type === 'info' ? '' : 'btn-close-white'} ms-2`}
                  aria-label="Close"
                  onClick={() => removeToast(t.id)}
                ></button>
              </div>
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};
